import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { Check, Plus, RotateCcw, SquarePlus, X } from 'lucide-react';

interface CreateViewButtonProps {
  onCreateView: (name: string) => void;
  isCreatingView?: boolean;
  className?: string;
}

const CreateViewButton: React.FC<CreateViewButtonProps> = ({
  onCreateView,
  isCreatingView = false,
  className = '',
}) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  const handleCreateView = async() => {
    if (newViewName.trim()) {
      await onCreateView(newViewName.trim());
      setNewViewName('');
      setIsCreateModalOpen(false);
    }
  };

  return (
    <div className={className}>
      <Dialog

        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      >
        <DialogTrigger asChild>
          <Button
            size='med'
            variant='ghost'
            onClick={() => {setIsCreateModalOpen(true);}}
            className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-201`}
          >
            <Plus className="h-4 w-4" />
            New
          </Button>
        </DialogTrigger>

        {isCreatingView ? (
          <DialogContent className="sm:max-w-md bg-white">
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-800 mb-1">
                  Creating View
                </p>
                <p className="text-sm text-gray-600">
                  This may take a moment...
                </p>
              </div>
            </div>
          </DialogContent>
        ) : (
          <DialogContent className="sm:max-w-md bg-white p-0 overflow-hidden border-none">
            <div className="bg-gray-201 p-3 font-bold flex items-center">
              <SquarePlus className="h-4 w-4 mr-2" />
              Create New View
            </div>
            <div className="">
              <div>
                <input
                  style={{width: 'calc(100% - 2rem)'}}
                  className="m-4 p-2 text-gray-900 rounded-none border-b-1 borer-t-0 border-gray-201 border-l-0 border-r-0 shadow-none w-full focus-visible:outline-0 focus-visible:ring-0"
                  placeholder="Enter view name..."
                  value={newViewName}
                  onChange={(e) => setNewViewName(e.target.value)}
                  disabled={isCreatingView}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isCreatingView) {
                      handleCreateView();
                    }
                  }}
                />
              </div>
              <div className="flex items-center">
                <Button
                  className="w-full bg-gray-200 rounded-none"
                  variant="ghost"
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setNewViewName('');
                  }}
                >
                  <X className="h-6 w-6" />
                </Button>
                <Button
                  className={`w-full rounded-none ${!newViewName.trim() || isCreatingView ? 'bg-gray-201' : 'bg-green-501 text-white'}`}
                  variant="ghost"
                  onClick={handleCreateView}
                  disabled={!newViewName.trim() || isCreatingView}
                >
                  {isCreatingView ? (
                    <>
                      <RotateCcw className="h-6 w-6 animate-spin" />
                    </>
                  ) : (
                    <Check className="h-6 w-6" />
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default CreateViewButton;